import type { ApiClient } from './ApiClient';
import type { MembershipRole } from './auth';
import type { SchoolYear } from './schoolYear';
import type { ClassLevel } from './class';

// Formes alignées sur POST /schools et PATCH /schools/:id (SCH-01/SCH-02,
// apps/api/src/routes/schools.ts) — seule source de vérité pour ces routes.
export interface SchoolAddress {
  region: string;
  department: string;
  commune: string;
  // SCH-33 : `street`/`number` sont OPTIONNELS à la création (`null` si jamais
  // renseignés) — de nombreuses adresses sénégalaises réelles n'ont ni numéro de
  // rue formel ni nom de rue normalisé (repères géographiques). `region`/
  // `department`/`commune` restent, eux, obligatoires.
  street: string | null;
  number: string | null;
}

export interface School {
  id: string;
  name: string;
  slug: string;
  // SCH-02 : chemin RELATIF (`/schools/:id/logo`, route PUBLIQUE sans
  // authentification) — `null` tant qu'aucun logo n'a été téléversé. Résoudre via
  // `apiClient.resolveAssetUrl(school.logoUrl)` avant de l'utiliser dans un
  // `<img src>` (voir ApiClient.ts).
  logoUrl: string | null;
  country: string;
  currency: string;
  // FND-19 : fuseau horaire de l'établissement, nom IANA (`Africa/Dakar` par
  // défaut). Pilote les comparaisons de date métier des jobs planifiés (bascule
  // OVERDUE, relances) et l'horodatage du reçu PDF — voir
  // apps/api/src/lib/timezone.ts.
  timezone: string;
  ninea: string;
  address: SchoolAddress;
  headmasterName: string;
  status: string;
  // SCH-10 : `true` si la commune a été saisie librement via l'échappatoire "Ma
  // commune n'est pas listée" (CreateSchool.tsx) — aucune source post-2013
  // fiable et vérifiable trouvée pour combler l'écart de communes du
  // référentiel (@monecolier/geo-senegal, voir son README) ; présence seule
  // signale une revue manuelle (voir apps/api/scripts/
  // list-schools-pending-geo-review.ts, côté Mon Ecolier).
  addressGeoReviewRequested: boolean;
  // SCH-34 : `true` si le NINEA a été auto-corrigé par la migration de ce
  // ticket (ancien format erroné, radical+suffixe tronqué — SCH-01) — signale
  // qu'une revue manuelle/correction du COFI réel reste possible (voir
  // apps/api/scripts/list-schools-pending-ninea-review.ts). Modifier le NINEA
  // après création reste HORS PÉRIMÈTRE (SCH-35, ticket séparé).
  nineaFormatReviewRequested: boolean;
  // SCH-11 : REMPLACE l'ancien `activeSchoolYear: string | null` (SCH-03) par
  // l'objet SchoolYear complet (dates incluses) — `null` tant que l'admin n'a pas
  // activé explicitement une année (voir GET /schools/:id, POST
  // /schools/:id/school-years/:schoolYearId/activate dans schoolYear.ts).
  activeSchoolYear: SchoolYear | null;
  createdAt: string;
}

export interface CreateSchoolPayload {
  name: string;
  // SCH-34 : `ninea` transporte le radical (obligatoire, chiffres uniquement,
  // longueur libre ≥7) ET le COFI optionnel (3 caractères, chiffre+lettre+
  // chiffre) DÉJÀ CONCATÉNÉS par l'appelant — voir apps/api/src/lib/ninea.ts
  // pour le détail complet du format et du parseur. Un COFI absent/incomplet
  // ne bloque jamais la création. Modifier le NINEA après création reste HORS
  // PÉRIMÈTRE (SCH-35, ticket séparé) — aucune route de complétion différée
  // n'existe (l'ancien `updateSchoolNinet()`/`PATCH .../ninet` a été retiré).
  ninea: string;
  addressRegion: string;
  addressDepartment: string;
  addressCommune: string;
  // SCH-33 : chaîne (potentiellement vide) TOUJOURS envoyée par CreateSchool.tsx —
  // le backend (POST /schools) traite une valeur absente OU vide après trim de
  // façon identique (`null` en base, jamais bloquant). Type resté `string` plutôt
  // qu'optionnel : reflète le comportement RÉEL de l'unique appelant existant.
  addressStreet: string;
  addressNumber: string;
  headmasterName: string;
  // SCH-10 : `true` UNIQUEMENT quand l'utilisateur a explicitement coché "Ma
  // commune n'est pas listée" (CreateSchool.tsx) — région/département restent
  // validés contre le référentiel dans ce cas, seule la commune (`addressCommune`
  // ci-dessus, alors du texte libre) échappe à la validation stricte. Absent/
  // `false` = comportement STRICT inchangé (SCH-09).
  geoReviewRequested?: boolean;
}

export interface CreateSchoolResponse {
  school: School;
  // AUT-26 : toujours `['SCHOOL_ADMIN']` à la création (voir routes/schools.ts),
  // mais la forme reste `roles` pour rester cohérente avec `Member`/`Invitation`.
  roles: MembershipRole[];
}

// `accessToken` explicite plutôt que de dépendre de `getAuthToken` (config
// ApiClient) — même raison que `getMe()` dans auth.ts : appelée juste après un
// `login()`/`register()+login()` réussi, au moment où le token fraîchement émis
// n'est pas forcément déjà reflété par un `getAuthToken` branché sur un état React.
export function createSchool(
  client: ApiClient,
  payload: CreateSchoolPayload,
  accessToken: string,
): Promise<CreateSchoolResponse> {
  return client.post<CreateSchoolResponse>('/schools', payload, { accessToken });
}

// SCH-03 : les trois cycles combinables en V1 — Lycée volontairement absent (décision
// produit du 21/07, voir apps/api/src/lib/cycles.ts, seule source de vérité côté
// backend).
export const CYCLES = ['PRESCOLAIRE', 'ELEMENTAIRE', 'COLLEGE'] as const;
export type Cycle = (typeof CYCLES)[number];

export interface GetSchoolResponse {
  school: School;
  activatedCycles: Cycle[];
}

export function getSchool(client: ApiClient, schoolId: string, accessToken: string): Promise<GetSchoolResponse> {
  return client.get<GetSchoolResponse>(`/schools/${schoolId}`, { accessToken });
}

export interface ActivateCycleResponse {
  cycle: Cycle;
}

export function activateCycle(
  client: ApiClient,
  schoolId: string,
  cycle: Cycle,
  accessToken: string,
): Promise<ActivateCycleResponse> {
  return client.post<ActivateCycleResponse>(`/schools/${schoolId}/cycles`, { cycle }, { accessToken });
}

export function deactivateCycle(
  client: ApiClient,
  schoolId: string,
  cycle: Cycle,
  accessToken: string,
): Promise<void> {
  return client.delete<void>(`/schools/${schoolId}/cycles/${cycle}`, { accessToken });
}

// SCH-11 : `PATCH /schools/:id/active-school-year` (chaîne libre, SCH-03) est
// RETIRÉ — remplacé par les fonctions POST/GET /schools/:id/school-years/* de
// schoolYear.ts (création, liste, activation/désactivation explicites, phases
// d'inscription).

// SCH-02 — personnalisation de l'espace (nom, devise, logo).

export interface UpdateSchoolBrandingPayload {
  name?: string;
  currency?: string;
  // FND-19 : nom IANA. Accepté à toute casse et sous ses alias — le backend
  // renvoie l'orthographe canonique de Postgres (400 `invalid_timezone` sinon).
  // Jamais verrouillé par l'existence de factures, contrairement à `currency`.
  timezone?: string;
}

export interface UpdateSchoolBrandingResponse {
  school: School;
}

/**
 * Au moins un des trois champs requis côté backend (400 sinon) — `currency` peut
 * être refusée (409 `currency_locked`) si l'établissement a déjà émis au moins une
 * facture, voir apps/api/src/routes/schools.ts.
 */
export function updateSchoolBranding(
  client: ApiClient,
  schoolId: string,
  payload: UpdateSchoolBrandingPayload,
  accessToken: string,
): Promise<UpdateSchoolBrandingResponse> {
  return client.patch<UpdateSchoolBrandingResponse>(`/schools/${schoolId}`, payload, { accessToken });
}

export interface UpdateSchoolLogoResponse {
  school: School;
}

/**
 * `file` : PNG/JPEG, max 2 Mo (validé RÉELLEMENT côté serveur par magic bytes,
 * jamais seulement côté client) — `ApiClient#post` détecte automatiquement un
 * corps `FormData` et laisse le navigateur poser lui-même `Content-Type:
 * multipart/form-data; boundary=...` (voir ApiClient.ts, même mécanisme
 * qu'ENR-07/`uploadEnrollmentDocument`).
 */
export function updateSchoolLogo(
  client: ApiClient,
  schoolId: string,
  file: File,
  accessToken: string,
): Promise<UpdateSchoolLogoResponse> {
  const formData = new FormData();
  formData.append('file', file);
  return client.post<UpdateSchoolLogoResponse>(`/schools/${schoolId}/logo`, formData, { accessToken });
}

// SCH-05 — assistant d'onboarding guidé. `ClassLevel` réutilisé de `./class`
// (SEULE source de vérité frontend, SCH-13) — jamais une seconde union recopiée
// ici.

export interface OnboardingStepStatus {
  complete: boolean;
  required: boolean;
}

export interface OnboardingClassesStepStatus extends OnboardingStepStatus {
  classCount: number;
}

export interface OnboardingFeesStepStatus extends OnboardingStepStatus {
  incompleteLevels: ClassLevel[];
}

export interface OnboardingStaffInviteStepStatus extends OnboardingStepStatus {
  invitationCount: number;
}

// SCH-27 — même famille que `staffInvite` (OPTIONNELLE, `required: false`,
// jamais comptée dans `completedRequiredSteps`) : `studentCount` compte TOUT
// `Student` du tenant, quel que soit le mécanisme de création (saisie
// manuelle SCH-06, import CSV SCH-18, transfert...) — voir
// apps/api/src/routes/schools.ts pour le détail complet.
export interface OnboardingStudentImportStepStatus extends OnboardingStepStatus {
  studentCount: number;
}

export interface OnboardingStatus {
  steps: {
    branding: OnboardingStepStatus;
    // SCH-22 : `School.activeSchoolYearId !== null` — voir apps/api/src/routes/
    // schools.ts pour le détail complet (aucune colonne persistée dédiée).
    schoolYear: OnboardingStepStatus;
    classes: OnboardingClassesStepStatus;
    // SCH-27 : positionnée après `classes` dans `STEP_KEYS`
    // (`apps/web-admin/src/lib/onboardingSteps.ts`) — les élèves ont besoin de
    // classes existantes pour être affectés — mais reste ici, dans l'ordre des
    // clés de cet objet `steps`, à côté des autres champs optionnels : l'ordre
    // des CLÉS de cet objet n'a jamais eu de signification (SEUL `STEP_KEYS`
    // ordonne le carrousel).
    studentImport: OnboardingStudentImportStepStatus;
    fees: OnboardingFeesStepStatus;
    staffInvite: OnboardingStaffInviteStepStatus;
  };
  completedRequiredSteps: number;
  totalRequiredSteps: number;
  percentComplete: number;
  enrollmentReady: boolean;
}

/**
 * GET /schools/:id/onboarding-status (SCH-05, apps/api/src/routes/schools.ts) —
 * les 6 étapes sont TOUTES dérivées EN TEMPS RÉEL depuis des données déjà
 * persistées ailleurs (logo/année scolaire active/classes/tarifs/invitations/
 * élèves, SCH-22 pour la deuxième, SCH-27 pour la dernière), jamais un
 * booléen mis en cache côté serveur — voir le commentaire de la route pour
 * le détail complet.
 * `SCHOOL_ADMIN_ONLY` côté backend.
 */
export function getOnboardingStatus(client: ApiClient, schoolId: string, accessToken: string): Promise<OnboardingStatus> {
  return client.get<OnboardingStatus>(`/schools/${schoolId}/onboarding-status`, { accessToken });
}

// FND-19 — liste des fuseaux horaires sélectionnables pour un établissement.

export interface ListTimezonesResponse {
  /** Noms IANA, triés, tels que Postgres les orthographie — la SEULE liste que
   * `POST /schools`/`PATCH /schools/:id` accepteront (voir
   * apps/api/src/routes/timezones.ts pour la divergence ICU/Postgres qui rend
   * cette route nécessaire plutôt qu'un `Intl.supportedValuesOf` côté client). */
  timezones: string[];
}

/** `requireAuth` seul côté backend — appelable AVANT d'avoir un Membership
 * (cas de `CreateSchool.tsx`), comme `getGeoSenegal()`. */
export function listTimezones(client: ApiClient, accessToken: string): Promise<ListTimezonesResponse> {
  return client.get<ListTimezonesResponse>('/timezones', { accessToken });
}
